import type { BaseSyntheticEvent } from 'react';
import styled from 'styled-components';
import { Loader } from '@elfsight/widget-ui';
import type {
  FieldType,
  FieldValue,
  FormField,
  FormThemeValue
} from '../contexts';
import { useFormSelector } from '../contexts';
import { LINE_HEIGHT_RATIO } from '../fields';
import { SubmitButton } from '../submit-button';
import type { ContentDirection } from '../types';
import { FieldComponent as Field } from './FieldComponent';
import { useDisabledButton } from './hooks';

const OUTSIDE_FIELD_TYPES = ['consent'] as FieldType[];
const OUTSIDE_LABEL_FIELD_TYPES = ['choice', 'dropdown'] as FieldType[];

export function HorizontalLayout({
  formSettings,
  theme,
  handleChange,
  handleSubmit,
  inputFieldTypes,
  ...forwardingProps
}: {
  formSettings: any;
  theme: FormThemeValue;
  handleSubmit?(e: BaseSyntheticEvent): Promise<void>;
  handleChange?(value?: FieldValue): void;
  inputFieldTypes?: FieldType[];
  hideErrors?: boolean;
  contentDirection?: ContentDirection;
}) {
  const {
    fields,
    fieldSpacing,
    fieldLabelPosition,
    fieldFontStyle,
    fieldBorderType,
    fieldBorderWidth,
    submitButtonProps,
    submitButtonStyle
  } = formSettings;

  const { accentColor, errorColor } = theme;
  const { status, fields: derivedFields } = useFormSelector();
  const disabledButton = useDisabledButton(derivedFields);

  const inlineFields = fields.filter(
    ({ type }: FormField) => !OUTSIDE_FIELD_TYPES.includes(type)
  );
  const outsideFields = fields.filter(({ type }: FormField) =>
    OUTSIDE_FIELD_TYPES.includes(type)
  );

  const rowHasLabelInside =
    fieldLabelPosition === 'inside' &&
    inlineFields.some(
      ({ showLabel, label, type }: FormField) =>
        showLabel && !!label && !OUTSIDE_LABEL_FIELD_TYPES.includes(type)
    );

  const fieldLineHeight = Math.round(
    fieldFontStyle?.fontSize * LINE_HEIGHT_RATIO
  );
  const fieldPadding = rowHasLabelInside
    ? fieldFontStyle?.fontSize
    : Math.round(fieldFontStyle?.fontSize * 0.66);
  const derivedBorderWidth = fieldBorderType ? fieldBorderWidth : 0;

  const buttonHeight =
    fieldLineHeight + 2 * (fieldPadding + derivedBorderWidth);

  const buttonPadding =
    (buttonHeight - submitButtonProps?.font?.fontSize * LINE_HEIGHT_RATIO) / 2;

  const fieldProps = {
    formSettings,
    inputFieldTypes,
    isHorizontal: true,
    rowHasLabelInside,
    errorColor,
    ...forwardingProps
  };

  const derivedButtonProps = {
    ...submitButtonProps,
    fullWidth: false,
    verticalPadding: submitButtonProps?.height
      ? submitButtonProps?.verticalPadding
      : buttonPadding,
    height: submitButtonProps?.height || buttonHeight,
    disabled: disabledButton,
    onClick: handleSubmit
  };

  const visibleFields = inlineFields.filter(
    ({ type }: FormField) => type !== 'hidden'
  );

  const hiddenFields = inlineFields.filter(
    ({ type }: FormField) => type === 'hidden'
  );

  return (
    <>
      <InsideGrid _spacing={fieldSpacing} _numFields={visibleFields.length}>
        {visibleFields.map((field: FormField) => (
          <StyledField key={field.id} field={field} {...fieldProps} />
        ))}
        <Buttons>
          {status === 'pending' && (
            <StyledLoader
              size={submitButtonProps?.height - 4 || 30}
              color={accentColor}
            />
          )}
          <HorizontalSubmitButton
            {...derivedButtonProps}
            style={submitButtonStyle}
            _spacing={fieldSpacing}
            _visible={status !== 'pending'}
          />
        </Buttons>
      </InsideGrid>

      {hiddenFields.map((field: FormField) => (
        <Field
          onChange={handleChange}
          key={field.id}
          field={field}
          {...fieldProps}
        />
      ))}

      {outsideFields?.length > 0 && (
        <OutsideGrid _spacing={fieldSpacing}>
          {outsideFields.map((field: FormField) => (
            <StyledField
              key={field.id}
              field={field}
              isOutside
              {...fieldProps}
            />
          ))}
        </OutsideGrid>
      )}
    </>
  );
}

const Grid = styled.div<{ _spacing: number }>`
  text-align: left;
`;

const InsideGrid = styled(Grid)<{
  _numFields: number;
}>`
  display: grid;
  grid-template-columns: repeat(${({ _numFields }) => _numFields}, 1fr) auto;
  grid-template-rows: repeat(3, max-content);
`;

const OutsideGrid = styled(Grid)`
  display: flex;
  flex-direction: column;
`;

const StyledField = styled(Field)`
  &:first-of-type {
    padding-left: 0;
  }

  &:last-of-type {
    padding-right: 0;
  }
`;

const HorizontalSubmitButton = styled(SubmitButton)<{
  _spacing: number;
  _visible: boolean;
}>`
  grid-row: 3/3;
  visibility: ${({ _visible }) => (_visible ? 'visible' : 'hidden')};
  margin-left: ${({ _spacing }) => _spacing / 2}px;
  margin-right: ${({ _spacing }) => _spacing / 2}px;
`;

const StyledLoader = styled(Loader)`
  position: absolute;
  grid-row: 3/3;
  height: 100%;
`;

const Buttons = styled.div`
  position: relative;
  display: grid;
  grid-row: span 3;
  grid-template-rows: subgrid;
`;
